import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  AlertTriangle,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  CalendarDays,
  ClipboardList,
  Package,
  CreditCard,
  CheckCircle,
  XCircle,
  ArrowRight,
  Truck,
  UserPlus,
  AlertCircle,
} from "lucide-react";

interface EnhancedDashboardStats {
  ordersThisMonth: number;
  ordersAllTime: number;
  revenueThisMonth: number;
  revenueAllTime: number;
  activeSubscriptions: number;
  pendingOrders: number;
  waitlistByState: { state: string; count: number }[];
  pendingOrdersDetails: {
    total: number;
    urgent: number;
    critical: number;
    orders: { id: string; name: string; email: string; style: string; createdAt: string; hoursOld: number }[];
  };
  revenueComparison: {
    today: number;
    weeklyAverage: number;
    monthlyAverage: number;
    todayVsWeekly: number;
    todayVsMonthly: number;
  };
  subscriptionHealth: {
    active: number;
    cancelled: number;
    churnRate: number;
    monthlyRecurringRevenue: number;
    recentCancellations: { id: string; customerName: string; cancelledAt: string }[];
  };
  upcomingDeliveries: {
    week: string;
    count: number;
    orders: { id: string; name: string; style: string; deliveryWeek: string }[];
  }[];
  waitlistGrowth: {
    total: number;
    thisWeek: number;
    lastWeek: number;
    growthRate: number;
    recentSignups: { email: string; state: string; createdAt: string }[];
  };
}

interface QuickAction {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  onClick?: () => void;
  variant?: "default" | "outline" | "secondary" | "destructive";
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function UrgencyBadge({ hoursOld }: { hoursOld: number }) {
  if (hoursOld >= 48) {
    return (
      <Badge variant="destructive" className="flex items-center gap-1">
        <AlertCircle className="h-3 w-3" />
        Critical ({hoursOld}h)
      </Badge>
    );
  }
  if (hoursOld >= 24) {
    return (
      <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-300 flex items-center gap-1">
        <AlertTriangle className="h-3 w-3" />
        Urgent ({hoursOld}h)
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="flex items-center gap-1">
      <Clock className="h-3 w-3" />
      {hoursOld}h ago
    </Badge>
  );
}

function TrendIndicator({ value, label }: { value: number; label: string }) {
  const isPositive = value >= 0;
  const Icon = isPositive ? TrendingUp : TrendingDown;
  const colorClass = isPositive ? "text-green-600" : "text-red-600";

  return (
    <div className={`flex items-center gap-1 text-sm ${colorClass}`}>
      <Icon className="h-4 w-4" />
      <span>{isPositive ? "+" : ""}{value}%</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

function StatCardSkeleton() {
  return (
    <Card className="p-6">
      <div className="space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-4 w-32" />
      </div>
    </Card>
  );
}

interface AdminDashboardHomeProps {
  onNavigateToTab: (tab: string) => void;
}

export function AdminDashboardHome({ onNavigateToTab }: AdminDashboardHomeProps) {
  const { data: stats, isLoading, error } = useQuery<EnhancedDashboardStats>({
    queryKey: ["/api/admin/stats/enhanced"],
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="p-6">
              <Skeleton className="h-48 w-full" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-destructive">
          <AlertTriangle className="h-5 w-5" />
          <p>Failed to load dashboard metrics. Please try again.</p>
        </div>
      </Card>
    );
  }

  const quickActions: QuickAction[] = [
    {
      label: "Review Pending Orders",
      icon: Clock,
      onClick: () => onNavigateToTab("orders"),
      variant: stats.pendingOrdersDetails.critical > 0 ? "destructive" : "default",
    },
    {
      label: "View Subscriptions",
      icon: CreditCard,
      onClick: () => onNavigateToTab("subscriptions"),
    },
    {
      label: "Export Waitlist",
      icon: ClipboardList,
      onClick: () => onNavigateToTab("waitlist"),
    },
    {
      label: "Manage Blocked Weeks",
      icon: CalendarDays,
      onClick: () => onNavigateToTab("blocked"),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Top Stats Row */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Pending Orders with Urgency */}
        <Card
          className={`p-6 cursor-pointer transition-colors hover:bg-muted/50 ${
            stats.pendingOrdersDetails.critical > 0
              ? "border-destructive/50 bg-destructive/5"
              : stats.pendingOrdersDetails.urgent > 0
              ? "border-orange-300 bg-orange-50"
              : ""
          }`}
          onClick={() => onNavigateToTab("orders")}
          data-testid="stat-pending-orders"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Pending Approval</p>
              <p className="mt-2 text-3xl font-semibold">{stats.pendingOrdersDetails.total}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {stats.pendingOrdersDetails.critical > 0 && (
                  <Badge variant="destructive" className="flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {stats.pendingOrdersDetails.critical} critical
                  </Badge>
                )}
                {stats.pendingOrdersDetails.urgent > 0 && (
                  <Badge variant="secondary" className="bg-orange-100 text-orange-800 border-orange-300">
                    {stats.pendingOrdersDetails.urgent} urgent
                  </Badge>
                )}
              </div>
            </div>
            <div className={`rounded-full p-3 ${
              stats.pendingOrdersDetails.critical > 0
                ? "bg-destructive/10"
                : stats.pendingOrdersDetails.urgent > 0
                ? "bg-orange-100"
                : "bg-primary/10"
            }`}>
              <Clock className={`h-5 w-5 ${
                stats.pendingOrdersDetails.critical > 0
                  ? "text-destructive"
                  : stats.pendingOrdersDetails.urgent > 0
                  ? "text-orange-600"
                  : "text-primary"
              }`} />
            </div>
          </div>
        </Card>

        {/* Today's Revenue */}
        <Card className="p-6" data-testid="stat-revenue-today">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Today's Revenue</p>
              <p className="mt-2 text-3xl font-semibold">{formatCurrency(stats.revenueComparison.today)}</p>
              <div className="mt-2 space-y-1">
                <TrendIndicator value={stats.revenueComparison.todayVsWeekly} label="vs weekly avg" />
              </div>
            </div>
            <div className="rounded-full bg-primary/10 p-3">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
          </div>
        </Card>

        {/* Active Subscriptions */}
        <Card
          className="p-6 cursor-pointer transition-colors hover:bg-muted/50"
          onClick={() => onNavigateToTab("subscriptions")}
          data-testid="stat-subscriptions"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Active Subscriptions</p>
              <p className="mt-2 text-3xl font-semibold">{stats.subscriptionHealth.active}</p>
              <div className="mt-2">
                <p className="text-sm text-muted-foreground">
                  MRR: {formatCurrency(stats.subscriptionHealth.monthlyRecurringRevenue)}
                </p>
                {stats.subscriptionHealth.churnRate > 0 && (
                  <Badge variant="outline" className="mt-1 text-orange-600 border-orange-300">
                    {stats.subscriptionHealth.churnRate}% churn
                  </Badge>
                )}
              </div>
            </div>
            <div className="rounded-full bg-primary/10 p-3">
              <Users className="h-5 w-5 text-primary" />
            </div>
          </div>
        </Card>

        {/* Waitlist Growth */}
        <Card
          className="p-6 cursor-pointer transition-colors hover:bg-muted/50"
          onClick={() => onNavigateToTab("waitlist")}
          data-testid="stat-waitlist"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Waitlist</p>
              <p className="mt-2 text-3xl font-semibold">{stats.waitlistGrowth.total}</p>
              <div className="mt-2">
                <TrendIndicator value={stats.waitlistGrowth.growthRate} label="this week" />
                <p className="text-sm text-muted-foreground mt-1">
                  +{stats.waitlistGrowth.thisWeek} signups this week
                </p>
              </div>
            </div>
            <div className="rounded-full bg-primary/10 p-3">
              <UserPlus className="h-5 w-5 text-primary" />
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Pending Orders Detail */}
        <Card className="p-6" data-testid="pending-orders-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Orders Awaiting Approval
            </h3>
            <Button variant="ghost" size="sm" onClick={() => onNavigateToTab("orders")}>
              View All
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {stats.pendingOrdersDetails.orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mb-3" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm text-muted-foreground">No orders pending approval</p>
            </div>
          ) : (
            <div className="space-y-3">
              {stats.pendingOrdersDetails.orders.slice(0, 5).map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{order.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{order.style}</p>
                  </div>
                  <UrgencyBadge hoursOld={order.hoursOld} />
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Upcoming Deliveries */}
        <Card className="p-6" data-testid="upcoming-deliveries-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Truck className="h-5 w-5 text-primary" />
              Upcoming Deliveries
            </h3>
          </div>

          {stats.upcomingDeliveries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CalendarDays className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="font-medium">No upcoming deliveries</p>
              <p className="text-sm text-muted-foreground">All orders have been delivered</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.upcomingDeliveries.map((week) => (
                <div key={week.week} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm">{week.week}</span>
                    <Badge variant="secondary">{week.count} orders</Badge>
                  </div>
                  <div className="space-y-1">
                    {week.orders.slice(0, 3).map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center gap-2 text-sm text-muted-foreground pl-2"
                      >
                        <span className="w-2 h-2 rounded-full bg-primary/50" />
                        <span className="truncate">{order.name} - {order.style}</span>
                      </div>
                    ))}
                    {week.orders.length > 3 && (
                      <p className="text-xs text-muted-foreground pl-4">
                        +{week.orders.length - 3} more
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Revenue Overview */}
        <Card className="p-6" data-testid="revenue-overview-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-primary" />
              Revenue Overview
            </h3>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(stats.revenueComparison.today)}</p>
                <p className="text-xs text-muted-foreground">Today</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(stats.revenueComparison.weeklyAverage)}</p>
                <p className="text-xs text-muted-foreground">Daily Avg (Week)</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-muted/30">
                <p className="text-2xl font-bold">{formatCurrency(stats.revenueComparison.monthlyAverage)}</p>
                <p className="text-xs text-muted-foreground">Daily Avg (Month)</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>All-time Revenue</span>
                <span className="font-semibold">{formatCurrency(stats.revenueAllTime)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>This Month</span>
                <span className="font-semibold">{formatCurrency(stats.revenueThisMonth)}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Total Orders</span>
                <span className="font-semibold">{stats.ordersAllTime}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Subscription Health */}
        <Card className="p-6" data-testid="subscription-health-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-primary" />
              Subscription Health
            </h3>
            <Button variant="ghost" size="sm" onClick={() => onNavigateToTab("subscriptions")}>
              Manage
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="text-2xl font-bold text-green-700">{stats.subscriptionHealth.active}</span>
                </div>
                <p className="text-xs text-green-600 mt-1">Active Subscriptions</p>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-200">
                <div className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  <span className="text-2xl font-bold text-red-700">{stats.subscriptionHealth.cancelled}</span>
                </div>
                <p className="text-xs text-red-600 mt-1">Cancelled</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Monthly Recurring Revenue</span>
                <span className="font-semibold text-green-600">
                  {formatCurrency(stats.subscriptionHealth.monthlyRecurringRevenue)}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Churn Rate</span>
                <span className={`font-semibold ${stats.subscriptionHealth.churnRate > 10 ? "text-red-600" : "text-muted-foreground"}`}>
                  {stats.subscriptionHealth.churnRate}%
                </span>
              </div>
            </div>

            {stats.subscriptionHealth.recentCancellations.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground mb-2">Recent Cancellations</p>
                <div className="space-y-1">
                  {stats.subscriptionHealth.recentCancellations.slice(0, 3).map((cancel) => (
                    <div key={cancel.id} className="flex items-center justify-between text-sm">
                      <span className="truncate text-muted-foreground">{cancel.customerName}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(cancel.cancelledAt)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="p-6" data-testid="quick-actions-panel">
        <h3 className="text-lg font-semibold mb-4">Quick Actions</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map((action) => (
            <Button
              key={action.label}
              variant={action.variant || "outline"}
              className="w-full justify-start gap-2 h-auto py-3"
              onClick={action.onClick}
            >
              <action.icon className="h-4 w-4" />
              {action.label}
            </Button>
          ))}
        </div>
      </Card>

      {/* Waitlist Growth Section */}
      <Card className="p-6" data-testid="waitlist-growth-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Waitlist Growth
          </h3>
          <Button variant="ghost" size="sm" onClick={() => onNavigateToTab("waitlist")}>
            View All
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-3xl font-bold">{stats.waitlistGrowth.thisWeek}</p>
                <p className="text-sm text-muted-foreground">This Week</p>
              </div>
              <div className="text-center p-4 rounded-lg bg-muted/30">
                <p className="text-3xl font-bold">{stats.waitlistGrowth.lastWeek}</p>
                <p className="text-sm text-muted-foreground">Last Week</p>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <TrendIndicator value={stats.waitlistGrowth.growthRate} label="growth" />
            </div>
          </div>

          <div>
            <p className="text-sm font-medium mb-2">Recent Signups</p>
            <div className="space-y-2">
              {stats.waitlistGrowth.recentSignups.slice(0, 5).map((signup, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between text-sm p-2 rounded bg-muted/30"
                >
                  <span className="truncate text-muted-foreground">{signup.email}</span>
                  <Badge variant="outline">{signup.state}</Badge>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
